import { ColumnTable, Icon } from '@octano/global-ui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { moneyFormatter } from '../../../../../../utils/currency';
import { formatDate } from '../../../../../../utils/dates';
import { format as formatPercentage } from '../../../../../../utils/percentage';
import { StatusBadge } from '../../shared/StatusBadge';
import { CollectionFeeRuleItem } from '../api';

const dictPrefix = 'financialPortal.settings.collectionFees';

interface Options {
  onEdit: (item: CollectionFeeRuleItem) => void;
  onDelete: (item: CollectionFeeRuleItem) => void;
}

export function useRuleColumns({
  onEdit,
  onDelete,
}: Options): ColumnTable<CollectionFeeRuleItem>[] {
  const { t } = useTranslation();

  return useMemo<ColumnTable<CollectionFeeRuleItem>[]>(
    () => [
      {
        columnName: 'mode',
        headerText: t(`${dictPrefix}.columns.mode`),
        cellFormat: ({ row }) => t(`${dictPrefix}.modes.${row.mode}`),
      },
      {
        columnName: 'percentage',
        headerText: t(`${dictPrefix}.columns.percentage`),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        // Una fila lleva porcentaje o monto, nunca los dos: el otro va N/A,
        // como en el XD.
        cellFormat: ({ row }) =>
          row.percentage === null
            ? t(`${dictPrefix}.columns.notApplicable`)
            : formatPercentage(row.percentage, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }),
      },
      {
        columnName: 'amount',
        headerText: t(`${dictPrefix}.columns.amount`),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) =>
          row.amount === null
            ? t(`${dictPrefix}.columns.notApplicable`)
            : moneyFormatter().format(row.amount),
      },
      {
        columnName: 'overdueDays',
        headerText: t(`${dictPrefix}.columns.overdueDays`),
        thClassName: 'text-center',
        tdClassName: 'text-center',
      },
      {
        columnName: 'validFrom',
        headerText: t(`${dictPrefix}.columns.validFrom`),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => formatDate(row.validFrom, 'DD/MM/YYYY'),
      },
      {
        columnName: 'isActive',
        headerText: t(`${dictPrefix}.columns.status`),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        cellFormat: ({ row }) => <StatusBadge isActive={row.isActive} />,
      },
      {
        columnName: 'id',
        headerText: t(`${dictPrefix}.columns.actions`),
        thClassName: 'text-center',
        tdClassName: 'text-center',
        width: 90,
        cellFormat: ({ row }) =>
          row.isEditable ? (
            <div className="d-flex justify-content-center" style={{ gap: 8 }}>
              <button
                type="button"
                className="btn p-0"
                aria-label={t(`${dictPrefix}.columns.edit`)}
                title={t(`${dictPrefix}.columns.edit`)}
                onClick={() => onEdit(row)}
              >
                <Icon size="15" name="edit" color="primary" />
              </button>
              <button
                type="button"
                className="btn p-0"
                aria-label={t(`${dictPrefix}.columns.delete`)}
                title={t(`${dictPrefix}.columns.delete`)}
                onClick={() => onDelete(row)}
              >
                <Icon size="15" name="trash" color="primary" />
              </button>
            </div>
          ) : null,
      },
    ],
    [t, onEdit, onDelete],
  );
}
